import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarIcon, FileText, Printer, Database, Save, Eye, Mail, Download, Trash2, FileDown, Sparkles, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generateWordDocument } from "@/utils/wordDocumentGenerator";
import { useSharedData } from "./AgreementsPage";
import cfgLogo from "@/assets/cfg-logo.png";

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  property_address: string;
  current_balance: number;
  payment_amount: number;
  interest_rate: number;
  first_payment_date: string;
  date_of_sale: string;
  created_at: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
}

export function AgreementGenerator() {
  const { agreementData, setAgreementData } = useSharedData();

  const [showPreview, setShowPreview] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [savedAgreements, setSavedAgreements] = useState<any[]>([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiCoverLetter, setAiCoverLetter] = useState("");

  // Currency formatting helper functions
  const formatCurrencyInput = (value: string) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    // Convert to number and format with commas
    const number = parseFloat(numericValue);
    if (isNaN(number)) return '';
    return number.toLocaleString('en-US');
  };

  const unformatCurrency = (formattedValue: string) => {
    // Remove commas and return the raw numeric string
    return formattedValue.replace(/,/g, '');
  };

  const handleCurrencyInputChange = (field: string, formattedValue: string) => {
    const numericValue = unformatCurrency(formattedValue);
    setAgreementData(prev => ({
      ...prev,
      [field]: numericValue
    }));
  };
  const { toast } = useToast();

  // Financial breakdown calculations
  const calculateFinancialBreakdown = (selectedDeal?: Deal) => {
    if (!selectedDeal) return null;

    // Use manual override if provided, otherwise try database fields
    const downPayment = agreementData.originalDownPayment ? 
      parseFloat(agreementData.originalDownPayment) : 
      (selectedDeal.down_payment_amount || selectedDeal.beginning_balance || 0);
    const monthlyPayment = selectedDeal.payment_amount || 0;
    const salesDate = selectedDeal.date_of_sale || selectedDeal.created_at;
    const firstPaymentDate = agreementData.firstPaymentDate || selectedDeal.first_payment_date;
    
    console.log('Calculating financial breakdown with:', {
      firstPaymentDate,
      monthlyPayment,
      salesDate
    });
    
    // Calculate months since sale to today
    const monthsSinceSale = salesDate ? 
      Math.max(0, Math.floor((new Date().getTime() - new Date(salesDate).getTime()) / (30.44 * 24 * 60 * 60 * 1000))) : 0;
    
    // Calculate months until first payment to buyer
    // This represents the number of monthly payments the seller will receive before transfer
    let monthsUntilFirstPayment = 0;
    if (firstPaymentDate) {
      try {
        // Parse the date - handle both ISO format and formatted strings like "January 01, 2026"
        let firstPayment: Date;
        
        // Try to parse the date string
        if (typeof firstPaymentDate === 'string') {
          firstPayment = new Date(firstPaymentDate);
        } else {
          firstPayment = firstPaymentDate;
        }
        
        if (isNaN(firstPayment.getTime())) {
          console.error('Invalid first payment date:', firstPaymentDate);
        } else {
          const today = new Date();
          
          // Calculate months between now and the first payment date
          // Set both to the 1st of their months for consistent comparison
          const firstPaymentMonth = new Date(firstPayment.getFullYear(), firstPayment.getMonth(), 1);
          const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          
          const yearDiff = firstPaymentMonth.getFullYear() - currentMonth.getFullYear();
          const monthDiff = firstPaymentMonth.getMonth() - currentMonth.getMonth();
          let totalMonthsDiff = (yearDiff * 12) + monthDiff;
          
          // The seller receives payments for all months BEFORE the first payment to buyer
          // If first payment to buyer is Jan 2025 and today is Oct 2024:
          // Nov 2024 and Dec 2024 payments go to seller = 2 payments
          // This is totalMonthsDiff (3) minus 1 = 2
          monthsUntilFirstPayment = Math.max(0, totalMonthsDiff - 1);
          
          console.log('Payment calculation details:', {
            todayDate: today.toLocaleDateString(),
            firstPaymentDateString: firstPaymentDate,
            firstPaymentParsed: firstPayment.toLocaleDateString(),
            todayMonth: `${today.getFullYear()}-${today.getMonth()}`,
            firstPaymentMonth: `${firstPayment.getFullYear()}-${firstPayment.getMonth()}`,
            yearDiff,
            monthDiff,
            totalMonthsDiff,
            monthsUntilFirstPayment,
            additionalCash: monthsUntilFirstPayment * monthlyPayment
          });
        }
      } catch (e) {
        console.error('Error parsing first payment date:', e, firstPaymentDate);
        monthsUntilFirstPayment = 0;
      }
    } else {
      console.warn('No first payment date provided for financial breakdown calculation');
    }

    const totalMonthlyPaymentsReceived = monthsSinceSale * monthlyPayment;
    const additionalPaymentsBeforeFirst = monthsUntilFirstPayment * monthlyPayment;
    const cashOfferAmount = parseFloat(agreementData.brokerOfferToSeller || '0');
    
    const totalCashFlow = downPayment + totalMonthlyPaymentsReceived + additionalPaymentsBeforeFirst + cashOfferAmount;

    return {
      downPayment,
      monthlyPayment,
      monthsSinceSale,
      totalMonthlyPaymentsReceived,
      monthsUntilFirstPayment,
      additionalPaymentsBeforeFirst,
      cashOfferAmount,
      totalCashFlow,
      salesDate
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const generateFinancialBreakdownText = () => {
    const selectedDeal = deals.find(deal => deal.id === selectedDealId);
    const breakdown = calculateFinancialBreakdown(selectedDeal);
    
    if (!breakdown || !selectedDeal) return '';

    return `FINANCIAL BREAKDOWN - TOTAL CASH RECEIVED

Property: ${agreementData.propertyAddress || selectedDeal.property_address}
Seller: ${agreementData.primarySellerName || selectedDeal.seller_name}

CASH FLOW SUMMARY:
• Initial Down Payment: ${formatCurrency(breakdown.downPayment)}
• Monthly Payments Received (${breakdown.monthsSinceSale} payments): ${formatCurrency(breakdown.totalMonthlyPaymentsReceived)}
• Additional Payments Before Transfer (${breakdown.monthsUntilFirstPayment} payments): ${formatCurrency(breakdown.additionalPaymentsBeforeFirst)}
• Cash Offer from Buyer: ${formatCurrency(breakdown.cashOfferAmount)}

TOTAL CASH RECEIVED: ${formatCurrency(breakdown.totalCashFlow)}

Monthly Payment Amount: ${formatCurrency(breakdown.monthlyPayment)}
Sale Date: ${breakdown.salesDate ? new Date(breakdown.salesDate).toLocaleDateString() : 'N/A'}
First Payment to New Buyer: ${agreementData.firstPaymentDate || 'N/A'}

This represents the total amount of money you will have received from this investment throughout its entire lifecycle, including your original down payment, all monthly payments collected to date, payments you'll receive before the transfer, and the final cash settlement.`;
  };
  
  // Fetch deals from database
  useEffect(() => {
    const fetchDeals = async () => {
      try {
      const { data, error } = await supabase
          .from('deals')
          .select('id, deal_title, seller_name, seller_email, property_address, current_balance, payment_amount, interest_rate, first_payment_date, first_payment_to_me, date_of_sale, created_at, sales_price, down_payment_amount, beginning_balance, payments_remaining')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        if (data) setDeals(data);
      } catch (error) {
        console.error('Error fetching deals:', error);
      }
    };
    
    fetchDeals();
    fetchSavedAgreements();
  }, []);

  // Fetch saved agreements from database
  const fetchSavedAgreements = async () => {
    try {
      const { data, error } = await supabase
        .from('agreements')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      if (data) setSavedAgreements(data);
    } catch (error) {
      console.error('Error fetching saved agreements:', error);
    }
  };

  const handleInputChange = (field: keyof typeof agreementData, value: any) => {
    setAgreementData(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle deal selection and populate form
  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    const selectedDeal = deals.find(deal => deal.id === dealId);
    
    if (selectedDeal) {
      // Format dates for display - using UTC to avoid timezone issues
      const formatDate = (dateStr: string) => {
        if (!dateStr) return "";
        try {
          // Parse as UTC to avoid timezone offset issues
          const dateParts = dateStr.split('T')[0]; // Get just the date part (YYYY-MM-DD)
          const [year, month, day] = dateParts.split('-').map(Number);
          const date = new Date(year, month - 1, day); // Create date in local timezone
          
          // Check if date is valid
          if (isNaN(date.getTime())) {
            return dateStr;
          }
          return format(date, "MMMM dd, yyyy");
        } catch {
          return dateStr;
        }
      };
      
      // Use first_payment_to_me as the date when CFG starts receiving payments
      // If not set, calculate it as next month from first_payment_date
      const calculateFirstPaymentToCFG = () => {
        // Check if first_payment_to_me exists
        if ((selectedDeal as any).first_payment_to_me) {
          return formatDate((selectedDeal as any).first_payment_to_me);
        }
        
        // Otherwise calculate from first_payment_date (add one month)
        if (selectedDeal.first_payment_date) {
          try {
            const dateParts = selectedDeal.first_payment_date.split('T')[0];
            const [year, month, day] = dateParts.split('-').map(Number);
            const originalDate = new Date(year, month - 1, day);
            
            if (isNaN(originalDate.getTime())) {
              return "";
            }
            const nextPaymentDate = new Date(year, month, day); // month is already 0-indexed + 1 = next month
            return format(nextPaymentDate, "MMMM dd, yyyy");
          } catch {
            return "";
          }
        }
        
        return "";
      };
      
      // Calculate date of balance (30 days before first payment to CFG)
      const calculateDateOfBalance = (firstPaymentToCFG: string) => {
        if (!firstPaymentToCFG) return "";
        try {
          const cfgPaymentDate = new Date(firstPaymentToCFG);
          if (isNaN(cfgPaymentDate.getTime())) {
            return "";
          }
          // 30 days before first payment to CFG
          const balanceDate = new Date(cfgPaymentDate);
          balanceDate.setDate(balanceDate.getDate() - 30);
          return format(balanceDate, "MMMM dd, yyyy");
        } catch {
          return "";
        }
      };
      
      const firstPaymentToCFG = calculateFirstPaymentToCFG();
      
      setAgreementData(prev => ({
        ...prev,
        primarySellerName: selectedDeal.seller_name || "",
        secondarySellerName: "",
        propertyAddress: selectedDeal.property_address || "",
        balance: selectedDeal.current_balance ? selectedDeal.current_balance.toString() : "",
        firstPaymentDate: firstPaymentToCFG,
        dateOfBalance: calculateDateOfBalance(firstPaymentToCFG),
        date: new Date(), // Set to today for the agreement
        recipientEmail: (selectedDeal as any).seller_email || "",
        paymentsRemaining: (selectedDeal as any).payments_remaining?.toString() || "",
      }));
    }
  };

  const generatePartialAgreement = () => {
    const sellerNames = agreementData.secondarySellerName 
      ? `${agreementData.primarySellerName || "________________"} and ${agreementData.secondarySellerName}`
      : agreementData.primarySellerName || "________________";
    
    return `Commitment to Purchase Agreement

This Purchase Agreement made and entered into ${agreementData.date ? format(agreementData.date, "MMMM dd, yyyy") : "________________"},
By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.
The property address is: ${agreementData.propertyAddress || "________________"}
SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:

1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.
Purchaser agrees to buy the next $${agreementData.partialDollarAmount || "________________"} due and you agree to sell the above described sum for $${agreementData.brokerOfferToSeller || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"} and ending with the payment due ${agreementData.partialEndDate || "________________"} assuming there are no unpaid fees or costs accumulated by the borrowers.

2. COSTS: PURCHASER will pay title and appraisal costs

3. SELLER warrants that all real property taxes that are due and payable have been paid.

4. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:
A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.
B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.

5. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.

6. SELLER shall keep and maintain documentation of any ${agreementData.paperType || "________________"} payments that are paid during the processing of this transaction.

7. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.


__________________________________    __________________________________________
${agreementData.primarySellerName || "Primary Seller"}                            Contract Funding Group, LLC

${agreementData.secondarySellerName ? `__________________________________\n${agreementData.secondarySellerName}` : ""}`;
  };

  const generateFullAgreement = () => {
    const sellerNames = agreementData.secondarySellerName 
      ? `${agreementData.primarySellerName || "________________"} and ${agreementData.secondarySellerName}`
      : agreementData.primarySellerName || "________________";
    
    return `Commitment to Purchase Agreement

This Purchase Agreement made and entered into ${agreementData.date ? format(agreementData.date, "MMMM dd, yyyy") : "________________"},

By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.

The property address is: ${agreementData.propertyAddress || "________________"}

SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:

1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.

2. PURCHASER agrees to buy the entire remaining balance of $${agreementData.balance || "________________"} and shall pay to SELLER $${agreementData.brokerOfferToSeller || "________________"} for the full purchase of said ${agreementData.paperType || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"}

3. COSTS: PURCHASER will pay title and appraisal costs

4. SELLER warrants that all real property taxes that are due and payable have been paid.

5. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:

A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.

B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.

6. PURCHASER has the exclusive right and option to examine the contract under the terms and conditions in this Agreement, if, within 30 days of the date of this agreement, after completing its examination the PURCHASER will notify the SELLER in writing of any defects or objections which relate to the ${agreementData.paperType || "________________"}, or anything related to property the PURCHASER deems significant and unsatisfactory. PURCHASER and SELLER will have 30 days from the date of the PURCHASER'S notice in which to cure the defects or correct the dissatisfactory condition in a manner acceptable to the PURCHASER and SELLER will bear all costs to cure defects.  If PURCHASER and SELLER are unable to cure the defects the Agreement shall be null and void and of no further force or effect and all documentation is to be returned to SELLER. Once having proceeded beyond 30 days PURCHASER and SELLER agree to close the sale of the above referenced ${agreementData.paperType || "________________"} in an acceptable fashion as soon as their respective examination have been completed.

7. This agreement is assignable.

8. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.


__________________________________    __________________________________________
${agreementData.primarySellerName || "Primary Seller"}                            Contract Funding Group, LLC

${agreementData.secondarySellerName ? `__________________________________\n${agreementData.secondarySellerName}` : ""}`;
  };

  const generatePrivateInvestorAgreement = () => {
    const sellerNames = agreementData.secondarySellerName 
      ? `${agreementData.primarySellerName || "________________"} and ${agreementData.secondarySellerName}`
      : agreementData.primarySellerName || "________________";
    
    const balanceDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    return `PURCHASE & SALE AGREEMENT
MI LAND CONTRACT FULL BUYOUT

This Agreement is executed between ${sellerNames} ("Seller" or "You") whose mailing address is ________________, and ${agreementData.buyerName || "Contract Funding Group, LLC"}, ("Buyer" or "Us"), whose business address is ________________.

COVENANTS

Buyer and Seller mutually agree and promise to the following:

1. The subject Land Contract (the term "Land Contract" for the purposes of this Agreement shall include any note or lien integrated with the Land Contract) is identified as:

   The purchaser under the Land Contract is ${agreementData.purchaserName || "________________"} ("Purchaser"),
   
   Dated ${agreementData.contractDate || "________________"},
   
   Having the current balance of $${agreementData.balance || "________________"} as of ${balanceDate},
   
   Interest of ${agreementData.interestRate || "________________"}%,
   
   Secured by property located at ${agreementData.propertyAddress || "________________"}

2. Promise to Buy/Sell. Seller promises to sell and Buyer promises to buy all of the remaining payments under the Land Contract, beginning with the payment due on ${agreementData.nextPaymentDue || "________________"}. Payable as follows: $${agreementData.purchaseAmount || "________________"} at closing, as set forth in the final closing statement.

3. Additional Covenants and Warranties by Seller. Seller certifies and warrants the following representations:

   a. Seller is the lawful owner of the Land Contract and has the right to sell the Land Contract without impediment.
   
   b. The Land Contract and all underlying security instruments are valid, enforceable, and unmodified, and all sums due under the Land Contract from Purchaser to Seller are current and in good standing.
   
   c. Seller will promptly execute all documents reasonably required to transfer the Land Contract to Buyer.
   
   d. Seller will assist Buyer in reviewing the property and finances of the Purchaser.
   
   e. The balance, terms, and payment history stated herein are true and accurate. Seller certifies there are no pending actions, suits, claims, or liens against the Property or Land Contract other than those expressly disclosed.
   
   f. The Property is free of any material defects, violations, and conditions that might affect its value or marketability, except as may be disclosed in writing to Buyer.

4. Closing. The parties agree that closing shall occur on or before ________________, unless mutually agreed to be extended. Buyer or its designee will coordinate scheduling and location. Time is of the essence. Seller will provide all necessary documentation before closing, including the original Land Contract, deed (if applicable), payment records, and other relevant materials.

5. Buyer's Rights to Due Diligence and Inspections. Buyer may conduct any investigations of the Property, Purchaser, and Land Contract as Buyer deems necessary, including but not limited to: title search, property inspection, appraisal, credit check, and review of financial records. Seller agrees to cooperate fully with these investigations and provide reasonable access and documentation. Buyer may assign this Agreement to a third party or designee at Buyer's sole discretion.

6. Default and Remedies. If Seller fails to perform obligations under this Agreement, Buyer may, at its option: (a) terminate this Agreement and seek damages, or (b) seek specific performance. If Buyer fails to close, Seller's sole remedy is to retain any earnest money deposit, if applicable, as liquidated damages. Liquidated damages are set at $${agreementData.liquidatedDamages || "________________"}.

7. Contingencies. This Agreement is contingent upon Buyer's satisfaction with: (a) the results of all due diligence and inspections, (b) verification of Purchaser's payment history and creditworthiness, and (c) obtaining satisfactory title commitment or title insurance. Buyer may waive any contingency in writing. If any contingency is not satisfied or waived within ________________ days, either party may terminate this Agreement.

8. Representations and Warranties. Each party represents and warrants that: (a) they have full power and authority to enter into this Agreement, (b) this Agreement is legally binding and enforceable, and (c) they are not subject to any conflicting obligations that would prevent performance under this Agreement.

9. Entire Agreement. This Agreement constitutes the entire understanding between the parties and supersedes all prior discussions, negotiations, and agreements. Any amendments must be in writing and signed by both parties.

10. Notices. All notices shall be in writing and delivered by certified mail, return receipt requested, or reputable courier to the addresses set forth herein.

11. Governing Law. This Agreement shall be governed by and construed in accordance with the laws of the State of Michigan.

12. Execution. This Agreement may be executed in counterparts, each of which shall be deemed an original.


SELLER:                                          BUYER:

____________________________________            ____________________________________
${agreementData.primarySellerName || "Seller Name"}                                ${agreementData.buyerName || "Contract Funding Group, LLC"}

Date: _______________                           Date: _______________

${agreementData.secondarySellerName ? `____________________________________\n${agreementData.secondarySellerName}\n\nDate: _______________\n` : ""}

Drafted by: ${agreementData.draftedBy || "________________"}

Reference Number: ${agreementData.referenceNumber || "________________"}`;
  };

  const handlePrintPackage = async () => {
    const agreementContent = agreementData.agreementType === "partial" 
      ? generatePartialAgreement() 
      : agreementData.agreementType === "private"
      ? generatePrivateInvestorAgreement()
      : generateFullAgreement();
    
    // Calculate financial breakdown
    const selectedDeal = deals.find(deal => deal.id === selectedDealId);
    const breakdown = calculateFinancialBreakdown(selectedDeal);
    
    // Convert logo to base64 for print window
    const logoBase64 = await fetch(cfgLogo)
      .then(res => res.blob())
      .then(blob => new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      }))
      .catch(() => '');
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Purchase Agreement Package - ${agreementData.primarySellerName}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.8; 
                margin: 0; 
                font-size: 13px;
              }
              .page {
                padding: 0.75in;
                box-sizing: border-box;
                position: relative;
              }
              .page-break {
                page-break-after: always;
              }
              .logo-header {
                text-align: center;
                margin-bottom: 30px;
              }
              .logo-img {
                max-width: 200px;
                height: auto;
                margin-bottom: 20px;
                display: block;
                margin-left: auto;
                margin-right: auto;
              }
              @media print {
                .logo-img {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
              .agreement-content { 
                white-space: pre-wrap; 
                font-family: Arial, sans-serif;
                line-height: 1.8;
                font-size: 13px;
              }
              .signature-section {
                margin-top: 40px;
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                width: 100%;
                flex-wrap: wrap;
              }
              .signature-line {
                border-bottom: 1px solid #000;
                width: 300px;
                height: 20px;
                display: inline-block;
                margin-bottom: 20px;
              }
              .signature-label {
                text-align: center;
                margin-top: 5px;
                font-size: 10px;
              }
              .signature-box {
                margin: 10px;
                min-width: 300px;
              }
              .financial-breakdown {
                border: 4px solid #1a1a1a;
                padding: 20px;
                margin: 30px 0;
                background: linear-gradient(to bottom right, #f9f9f9, #ffffff);
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              }
              .total-cash-header {
                text-align: center;
                margin-bottom: 15px;
              }
              .total-amount {
                font-size: 32px;
                font-weight: 900;
                color: #1a1a1a;
                margin: 8px 0;
              }
              .breakdown-item {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                border-bottom: 1px solid #e0e0e0;
              }
              .breakdown-item-label {
                font-weight: 500;
                color: #333;
              }
              .breakdown-item-value {
                font-weight: 700;
                color: #1a1a1a;
              }
              .breakdown-highlight {
                background: #f0f0f0;
                padding: 12px;
                margin: 8px 0;
                border-radius: 4px;
              }
              .breakdown-total-footer {
                background: #1a1a1a;
                color: white;
                padding: 15px 20px;
                margin: 15px -20px -20px -20px;
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              .timeline-container {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin: 20px 0;
                padding: 20px;
                border: 2px solid #e0e0e0;
                background: white;
              }
              .timeline-step {
                text-align: center;
                flex: 1;
              }
              .timeline-number {
                width: 32px;
                height: 32px;
                background: #1a1a1a;
                color: white;
                border-radius: 50%;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                margin-bottom: 8px;
              }
              .timeline-divider {
                flex: 1;
                height: 2px;
                background: repeating-linear-gradient(
                  to right,
                  #999 0,
                  #999 5px,
                  transparent 5px,
                  transparent 10px
                );
                margin: 0 10px;
                position: relative;
                top: -20px;
              }
              @media print {
                @page {
                  size: letter;
                  margin: 0.75in;
                }
                body { 
                  margin: 0;
                  font-size: 13px;
                }
                .page {
                  padding: 0;
                  margin: 0;
                }
                .page-break {
                  page-break-after: always;
                }
                * {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <!-- Cover Letter Page -->
            <div class="page">
              <div style="text-align: center; margin-bottom: 40px; padding-bottom: 20px;">
                <img src="${logoBase64}" alt="Contract Funding Group Logo" class="logo-img" onerror="this.style.display='none'" />
                <div style="font-size: 16px; font-weight: bold; color: #1a1a1a; margin-bottom: 8px;">Mike Watters</div>
                <div style="font-size: 20px; font-weight: bold; color: #1a1a1a; letter-spacing: 1px;">CONTRACT FUNDING GROUP, LLC</div>
              </div>
              
              <div style="text-align: right; margin-bottom: 30px; font-size: 11px; color: #666;">
                ${agreementData.date ? format(agreementData.date, "MMMM dd, yyyy") : ""}
              </div>
              
              <div style="margin-bottom: 30px;">
                <div style="font-weight: bold; color: #1a1a1a;">${agreementData.primarySellerName}</div>
                <div style="font-size: 11px; color: #666;">Land Contract Holder</div>
              </div>
              
              <div style="margin-bottom: 30px;">
                <div style="font-weight: bold;">RE: Purchase Proposal</div>
                <div style="font-size: 11px; color: #666;">Property: ${agreementData.propertyAddress}</div>
              </div>
              
              <div style="margin-bottom: 20px; font-size: 12px; line-height: 1.8;">
                <p style="margin-bottom: 15px;">Dear ${agreementData.primarySellerName},</p>
                <p style="margin-bottom: 15px;">We are pleased to present our formal purchase proposal for the land contract on the property referenced above. After careful review and evaluation of your contract, we are prepared to move forward with a cash purchase offer.</p>
                <p style="margin-bottom: 15px;"><strong>This offer represents a comprehensive solution that provides you with immediate liquidity while recognizing the total value you've built in this investment.</strong></p>
              </div>
              
              ${breakdown ? `
              <div class="financial-breakdown">
                <div class="total-cash-header">
                  <div style="font-weight: bold; font-size: 16px; text-transform: uppercase; letter-spacing: 1px; color: #1a1a1a; margin-bottom: 8px;">
                    💰 Total Cash You Will Receive 💰
                  </div>
                  <div class="total-amount">${formatCurrency(breakdown.totalCashFlow)}</div>
                  <div style="font-size: 10px; color: #666; font-style: italic;">Complete Cash Flow Throughout Investment Lifecycle</div>
                </div>
                
                <div style="border-top: 2px solid #ccc; margin: 15px 0;"></div>
                
                <div style="margin: 15px 0;">
                  <div class="breakdown-item">
                    <span class="breakdown-item-label">Initial Down Payment</span>
                    <span class="breakdown-item-value">${formatCurrency(breakdown.downPayment)}</span>
                  </div>
                  <div class="breakdown-item">
                    <span class="breakdown-item-label">Monthly Payments Received to Date <span style="font-size: 10px; color: #666;">(${breakdown.monthsSinceSale} payments)</span></span>
                    <span class="breakdown-item-value">${formatCurrency(breakdown.totalMonthlyPaymentsReceived)}</span>
                  </div>
                  ${breakdown.monthsUntilFirstPayment > 0 ? `
                  <div class="breakdown-item">
                    <span class="breakdown-item-label">Future Payments Before Transfer <span style="font-size: 10px; color: #666;">(${breakdown.monthsUntilFirstPayment} payments)</span></span>
                    <span class="breakdown-item-value">${formatCurrency(breakdown.additionalPaymentsBeforeFirst)}</span>
                  </div>
                  ` : ''}
                  <div class="breakdown-highlight">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                      <span style="font-weight: bold; color: #1a1a1a;">Cash Offer from Contract Funding Group</span>
                      <span style="font-weight: 900; font-size: 18px; color: #1a1a1a;">${formatCurrency(breakdown.cashOfferAmount)}</span>
                    </div>
                  </div>
                </div>
                
                <div class="breakdown-total-footer">
                  <span style="font-weight: bold; font-size: 14px; text-transform: uppercase;">TOTAL CASH RECEIVED</span>
                  <span style="font-weight: 900; font-size: 22px;">${formatCurrency(breakdown.totalCashFlow)}</span>
                </div>
              </div>
              ` : ''}
              
              <div style="border: 2px solid #1a1a1a; padding: 20px; margin: 25px 0; background: #f9f9f9;">
                <h3 style="color: #1a1a1a; margin-bottom: 15px; font-size: 14px; border-bottom: 1px solid #ccc; padding-bottom: 8px;">
                  PURCHASE PROPOSAL DETAILS
                </h3>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; font-size: 11px;">
                  <div>
                    <div style="color: #666; text-transform: uppercase; font-size: 9px; margin-bottom: 4px;">Property Address</div>
                    <div style="font-weight: 600;">${agreementData.propertyAddress}</div>
                  </div>
                  <div>
                    <div style="color: #666; text-transform: uppercase; font-size: 9px; margin-bottom: 4px;">Purchase Type</div>
                    <div style="font-weight: 600;">${agreementData.agreementType === "partial" ? "Partial Purchase" : agreementData.agreementType === "private" ? "Private Investor" : "Full Purchase"}</div>
                  </div>
                  <div>
                    <div style="color: #666; text-transform: uppercase; font-size: 9px; margin-bottom: 4px;">Current Balance</div>
                    <div style="font-weight: 700; font-size: 14px;">${formatCurrency(parseFloat(agreementData.balance || "0"))}</div>
                  </div>
                  <div>
                    <div style="color: #666; text-transform: uppercase; font-size: 9px; margin-bottom: 4px;">Our Offer</div>
                    <div style="font-weight: 700; font-size: 14px;">${formatCurrency(parseFloat(agreementData.brokerOfferToSeller || "0"))}</div>
                  </div>
                  ${agreementData.agreementType === "partial" && agreementData.partialDollarAmount ? `
                  <div style="grid-column: span 2;">
                    <div style="color: #666; text-transform: uppercase; font-size: 9px; margin-bottom: 4px;">Partial Purchase Amount</div>
                    <div style="font-weight: 600;">${formatCurrency(parseFloat(agreementData.partialDollarAmount))}</div>
                  </div>
                  ` : ''}
                </div>
              </div>
              
              <div class="timeline-container">
                <div class="timeline-step">
                  <div class="timeline-number">1</div>
                  <div style="font-weight: 600; font-size: 10px; margin-bottom: 4px;">Due Diligence</div>
                  <div style="font-size: 9px; color: #666;">Review & Verification</div>
                </div>
                <div class="timeline-divider"></div>
                <div class="timeline-step">
                  <div class="timeline-number">2</div>
                  <div style="font-weight: 600; font-size: 10px; margin-bottom: 4px;">Title & Payment</div>
                  <div style="font-size: 9px; color: #666;">Verification Process</div>
                </div>
                <div class="timeline-divider"></div>
                <div class="timeline-step">
                  <div class="timeline-number">3</div>
                  <div style="font-weight: 600; font-size: 10px; margin-bottom: 4px;">Closing</div>
                  <div style="font-size: 9px; color: #666;">Funds Transfer</div>
                </div>
              </div>
              
              <div style="border: 1px solid #ccc; padding: 15px; margin: 20px 0;">
                <h3 style="font-weight: bold; color: #1a1a1a; margin-bottom: 12px; font-size: 12px;">Required Documents</h3>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 10px;">
                  <div>• Original land contract</div>
                  <div>• Complete payment history</div>
                  <div>• Property tax records</div>
                  <div>• Insurance documentation</div>
                  <div style="grid-column: span 2;">• Borrower contact information</div>
                </div>
              </div>
              
              <div style="font-size: 12px; line-height: 1.8; margin: 20px 0;">
                <p style="margin-bottom: 15px;"><strong>Next Steps:</strong></p>
                <p style="margin-bottom: 15px;">The attached Purchase Agreement outlines the complete terms and conditions of this offer. Upon your acceptance, we will immediately begin the due diligence process, which typically takes 30-45 days to complete. During this time, we will verify all documentation, conduct title searches, and ensure everything is in order for a smooth closing.</p>
                <p style="margin-bottom: 15px;">This proposal is valid for 30 days from the date above. To proceed, simply sign the attached Purchase Agreement and return it to us along with the required documents listed above.</p>
                <p style="margin-bottom: 15px;"><strong>Why Choose Contract Funding Group?</strong></p>
                <ul style="margin-left: 20px; margin-bottom: 15px;">
                  <li>Over 15 years of experience in land contract purchases</li>
                  <li>Fast, professional closing process (30-45 days)</li>
                  <li>No hidden fees - we cover all title and appraisal costs</li>
                  <li>Personalized service and transparent communication throughout</li>
                </ul>
              </div>
              
              <div style="font-size: 12px; line-height: 1.8; margin-bottom: 15px;">
                <p>We look forward to working with you on this transaction. Should you have any questions or require additional information, please do not hesitate to contact us at <strong>info@contractfundinggroup.com</strong> or by phone.</p>
              </div>
              
              <div style="margin-top: 50px; padding-top: 20px;">
                <p style="font-size: 12px; margin-bottom: 60px;">Sincerely,</p>
                <div>
                  <div style="border-bottom: 2px solid #1a1a1a; width: 250px; height: 50px;"></div>
                  <div style="font-weight: bold; margin-top: 4px;">Mike Watters</div>
                  <div style="font-weight: bold; margin-top: 2px;">Contract Funding Group, LLC</div>
                  <div style="font-size: 10px; color: #666; margin-top: 2px;">Authorized Representative</div>
                </div>
              </div>
              
              <div style="text-align: center; margin-top: 50px; padding-top: 20px; border-top: 1px solid #ccc; font-size: 10px; color: #666;">
                <div style="font-weight: 600; color: #1a1a1a;">Mike Watters</div>
                <div style="font-weight: 600; color: #1a1a1a; margin-top: 2px;">Contract Funding Group, LLC</div>
                <div style="margin-top: 4px;">info@contractfundinggroup.com</div>
              </div>
            </div>
            
            <!-- Agreement Page -->
            <div class="page page-break">
              <div class="logo-header">
                <img src="/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" alt="Contract Funding Group Logo" class="logo-img" />
              </div>
              <div class="agreement-content">${agreementContent.replace(/\n\n__________________________________.*[\s\S]*$/, '')}</div>
              <div class="signature-section">
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-label">${agreementData.primarySellerName || "Primary Seller"}</div>
                </div>
                ${agreementData.secondarySellerName ? `
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-label">${agreementData.secondarySellerName}</div>
                </div>` : ''}
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-label">Contract Funding Group, LLC</div>
                </div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handlePrint = () => {
    const agreementContent = agreementData.agreementType === "partial" 
      ? generatePartialAgreement() 
      : agreementData.agreementType === "private"
      ? generatePrivateInvestorAgreement()
      : generateFullAgreement();
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Purchase Agreement</title>
            <style>
              @page {
                size: letter;
                margin: 0.5in 0.75in;
              }
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.6; 
                margin: 0; 
                padding: 0;
                font-size: 11px;
              }
              .logo-header {
                text-align: center;
                margin-bottom: 20px;
              }
              .logo-img {
                max-width: 300px;
                height: auto;
                margin-bottom: 15px;
              }
              .agreement-content { 
                white-space: pre-wrap; 
                font-family: Arial, sans-serif;
                line-height: 1.6;
                font-size: 11px;
              }
              .signature-section {
                margin-top: 30px;
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                width: 100%;
                flex-wrap: wrap;
              }
              .signature-line {
                border-bottom: 1px solid #000;
                width: 300px;
                height: 20px;
                display: inline-block;
                margin-bottom: 20px;
              }
              .signature-label {
                text-align: center;
                margin-top: 5px;
                font-size: 10px;
              }
              .signature-box {
                margin: 10px;
                min-width: 300px;
              }
            </style>
          </head>
          <body>
            <div class="logo-header">
              <img src="/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" alt="Contract Funding Group Logo" class="logo-img" />
            </div>
            <div class="agreement-content">${agreementContent.replace(/\n\n__________________________________.*[\s\S]*$/, '')}</div>
            <div class="signature-section">
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">${agreementData.primarySellerName || "Primary Seller"}</div>
              </div>
              ${agreementData.secondarySellerName ? `
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">${agreementData.secondarySellerName}</div>
              </div>` : ''}
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">Contract Funding Group, LLC</div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleSaveAgreement = async () => {
    if (!agreementData.date || !agreementData.primarySellerName || !agreementData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in at least the date, primary seller name, and property address before saving.",
        variant: "destructive",
      });
      return;
    }

    try {
      const agreementContent = agreementData.agreementType === "partial" 
        ? generatePartialAgreement() 
        : agreementData.agreementType === "private"
        ? generatePrivateInvestorAgreement()
        : generateFullAgreement();

      const sellerNames = agreementData.secondarySellerName 
        ? `${agreementData.primarySellerName} and ${agreementData.secondarySellerName}`
        : agreementData.primarySellerName;

      const agreementDataToSave = {
        agreement_type: agreementData.agreementType,
        agreement_date: agreementData.date ? format(agreementData.date, 'yyyy-MM-dd') : null,
        seller_name: sellerNames,
        seller_address: agreementData.brokerCompanyName, // Using broker as seller address for now
        buyer_name: agreementData.brokerCompanyName,
        buyer_address: "", // Could be added as a field later
        property_address: agreementData.propertyAddress,
        purchase_price: agreementData.balance ? parseFloat(agreementData.balance) : null,
        down_payment: null, // Could be added as a field later
        balance_due: agreementData.balance ? parseFloat(agreementData.balance) : null,
        interest_rate: null, // Could be added as a field later
        monthly_payment: null, // Could be added as a field later
        final_payment_date: agreementData.firstPaymentDate ? agreementData.firstPaymentDate : null,
        partial_purchase_amount: agreementData.partialDollarAmount ? parseFloat(agreementData.partialDollarAmount) : null,
        partial_monthly_payment: null, // Could be added as a field later
        partial_final_date: agreementData.partialEndDate ? agreementData.partialEndDate : null,
        agreement_content: agreementContent,
      };

      const { error } = await supabase
        .from('agreements')
        .insert([agreementDataToSave]);

      if (error) throw error;

      toast({
        title: "Agreement Saved",
        description: "The agreement has been successfully saved to the database.",
      });

      // Refresh the saved agreements list
      fetchSavedAgreements();
    } catch (error) {
      console.error('Error saving agreement:', error);
      toast({
        title: "Save Failed",
        description: "There was an error saving the agreement. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAgreement = async (agreementId: string, sellerName: string) => {
    try {
      const { error } = await supabase
        .from('agreements')
        .delete()
        .eq('id', agreementId);

      if (error) throw error;

      toast({
        title: "Agreement deleted",
        description: `Agreement for "${sellerName}" has been successfully deleted`,
      });

      fetchSavedAgreements(); // Refresh the list
    } catch (error) {
      console.error('Error deleting agreement:', error);
      toast({
        title: "Error deleting agreement",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const viewSavedAgreement = (agreement: any) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Saved Agreement - ${agreement.seller_name}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.6; 
                margin: 40px; 
                font-size: 12px;
              }
              .agreement-content { 
                white-space: pre-wrap; 
                font-family: Arial, sans-serif; 
              }
            </style>
          </head>
          <body>
            <div class="agreement-content">${agreement.agreement_content}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleEmailAgreement = async () => {
    if (!agreementData.recipientEmail) {
      toast({
        title: "Email Required",
        description: "Please enter an email address to send the agreement.",
        variant: "destructive",
      });
      return;
    }

    if (!agreementData.date || !agreementData.primarySellerName || !agreementData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in at least the date, primary seller name, and property address before sending.",
        variant: "destructive",
      });
      return;
    }

    try {
      const agreementContent = agreementData.agreementType === "partial" 
        ? generatePartialAgreement() 
        : agreementData.agreementType === "private"
        ? generatePrivateInvestorAgreement()
        : generateFullAgreement();

      const sellerNames = agreementData.secondarySellerName 
        ? `${agreementData.primarySellerName} and ${agreementData.secondarySellerName}`
        : agreementData.primarySellerName;

      // Create HTML version of the agreement with logo
      const htmlContent = `
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${window.location.origin}/lovable-uploads/6f8ada21-9585-40cc-a3f6-caaacb36b3d6.png" 
               alt="Contract Funding Group Logo" style="max-width: 200px; height: auto;" />
        </div>
        <div style="font-family: Arial, sans-serif; line-height: 1.6; white-space: pre-wrap;">
          ${agreementContent.replace(/\n/g, '<br>')}
        </div>
      `;

      // Call edge function to send email
      const { error } = await supabase.functions.invoke('send-agreement-email', {
        body: {
          recipientEmail: agreementData.recipientEmail,
          recipientName: sellerNames,
          subject: `Purchase Agreement - ${agreementData.propertyAddress}`,
          htmlContent: htmlContent,
          agreementType: agreementData.agreementType
        }
      });

      if (error) throw error;

      toast({
        title: "Email Sent",
        description: `Agreement has been sent to ${agreementData.recipientEmail}`,
      });
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Email Failed",
        description: "There was an error sending the email. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateCoverLetter = async () => {
    const selectedDeal = deals.find(deal => deal.id === selectedDealId);
    if (!selectedDeal) {
      toast({
        title: "No Deal Selected",
        description: "Please select a deal first.",
        variant: "destructive",
      });
      return;
    }

    setAiGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-agreement-content', {
        body: {
          type: 'cover_letter',
          dealData: {
            sellerName: agreementData.primarySellerName || selectedDeal.seller_name,
            propertyAddress: agreementData.propertyAddress || selectedDeal.property_address,
            balance: agreementData.balance || selectedDeal.current_balance?.toString(),
            offerAmount: agreementData.brokerOfferToSeller,
            partialAmount: agreementData.partialDollarAmount,
          },
          agreementType: agreementData.agreementType,
        },
      });

      if (error) throw error;
      
      setAiCoverLetter(data.content);
      toast({
        title: "AI Cover Letter Generated! ✨",
        description: "Your personalized cover letter is ready.",
      });
    } catch (error) {
      console.error('Error generating cover letter:', error);
      toast({
        title: "Generation Failed",
        description: "There was an error generating the cover letter. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleReviewAgreement = async () => {
    const selectedDeal = deals.find(deal => deal.id === selectedDealId);
    if (!selectedDeal) {
      toast({
        title: "No Deal Selected",
        description: "Please select a deal first.",
        variant: "destructive",
      });
      return;
    }

    setAiGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-agreement-content', {
        body: {
          type: 'agreement_review',
          dealData: {
            sellerName: agreementData.primarySellerName || selectedDeal.seller_name,
            propertyAddress: agreementData.propertyAddress || selectedDeal.property_address,
            balance: agreementData.balance || selectedDeal.current_balance?.toString(),
            offerAmount: agreementData.brokerOfferToSeller,
            partialAmount: agreementData.partialDollarAmount,
          },
          agreementType: agreementData.agreementType,
        },
      });

      if (error) throw error;
      
      setAiCoverLetter(data.content);
      toast({
        title: "AI Review Complete! ✨",
        description: "Agreement suggestions are ready.",
      });
    } catch (error) {
      console.error('Error reviewing agreement:', error);
      toast({
        title: "Review Failed",
        description: "There was an error reviewing the agreement. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!agreementData.date || !agreementData.primarySellerName || !agreementData.propertyAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in at least the date, primary seller name, and property address before generating Word document.",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Generating Document",
        description: "Creating Word document...",
      });

      const blob = await generateWordDocument(agreementData);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Purchase_Agreement_${agreementData.primarySellerName.replace(/\s+/g, '_')}_${format(agreementData.date, 'yyyy-MM-dd')}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download Ready",
        description: "Word document has been generated and downloaded.",
      });
    } catch (error) {
      console.error('Error generating Word document:', error);
      toast({
        title: "Generation Failed",
        description: "There was an error generating the Word document. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Purchase Agreement Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Load from Deals */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Load from Deals Database
            </Label>
            <Select value={selectedDealId} onValueChange={handleDealSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a deal to auto-populate form..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title} - {deal.seller_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Agreement Type</Label>
            <Select 
              value={agreementData.agreementType} 
              onValueChange={(value: "partial" | "full" | "private") => handleInputChange("agreementType", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="partial">Partial Purchase Agreement</SelectItem>
                <SelectItem value="full">Full Purchase Agreement</SelectItem>
                <SelectItem value="private">Private Investor Agreement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Agreement Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !agreementData.date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {agreementData.date ? format(agreementData.date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={agreementData.date}
                    onSelect={(date) => handleInputChange("date", date)}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Primary Seller Name</Label>
              <Input
                value={agreementData.primarySellerName}
                onChange={(e) => handleInputChange("primarySellerName", e.target.value)}
                placeholder="Enter primary seller name"
              />
            </div>

            <div className="space-y-2">
              <Label>Secondary Seller Name (Optional)</Label>
              <Input
                value={agreementData.secondarySellerName}
                onChange={(e) => handleInputChange("secondarySellerName", e.target.value)}
                placeholder="Enter secondary seller name (e.g., spouse)"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Address (for sending agreement)</Label>
              <Input
                type="email"
                value={agreementData.recipientEmail}
                onChange={(e) => handleInputChange("recipientEmail", e.target.value)}
                placeholder="Enter email address"
              />
            </div>

            <div className="space-y-2">
              <Label>Broker/Company Name</Label>
              <Input
                value={agreementData.brokerCompanyName}
                onChange={(e) => handleInputChange("brokerCompanyName", e.target.value)}
                placeholder="Contract Funding Group, LLC"
              />
            </div>

            <div className="space-y-2">
              <Label>Paper Type</Label>
              <Input
                value={agreementData.paperType}
                onChange={(e) => handleInputChange("paperType", e.target.value)}
                placeholder="e.g., land contract, promissory note"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Property Address</Label>
            <Textarea
              rows={2}
              value={agreementData.propertyAddress}
              onChange={(e) => handleInputChange("propertyAddress", e.target.value)}
              placeholder="Enter complete property address"
            />
          </div>

          {/* Purchase Details - Shared Fields */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Purchase Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Current Balance</Label>
                <Input
                  value={formatCurrencyInput(agreementData.balance)}
                  onChange={(e) => handleCurrencyInputChange("balance", e.target.value)}
                  placeholder="Enter current balance amount"
                />
              </div>

              <div className="space-y-2">
                <Label>Date of Balance</Label>
                <Input
                  value={agreementData.dateOfBalance}
                  onChange={(e) => handleInputChange("dateOfBalance", e.target.value)}
                  placeholder="Enter date of balance"
                />
              </div>

              <div className="space-y-2">
                <Label>Broker Offer to Seller</Label>
                <Input
                  value={formatCurrencyInput(agreementData.brokerOfferToSeller)}
                  onChange={(e) => handleCurrencyInputChange("brokerOfferToSeller", e.target.value)}
                  placeholder="Enter offer amount"
                />
              </div>

              <div className="space-y-2">
                <Label>First Payment Date</Label>
                <Input
                  value={agreementData.firstPaymentDate}
                  onChange={(e) => handleInputChange("firstPaymentDate", e.target.value)}
                  placeholder="Enter first payment date"
                />
              </div>

              <div className="space-y-2">
                <Label>Original Down Payment (if different from database)</Label>
                <Input
                  value={formatCurrencyInput(agreementData.originalDownPayment || '')}
                  onChange={(e) => handleCurrencyInputChange("originalDownPayment", e.target.value)}
                  placeholder="Enter actual down payment received"
                />
              </div>

              <div className="space-y-2">
                <Label>Payments Remaining</Label>
                <Input
                  type="number"
                  value={agreementData.paymentsRemaining || ''}
                  onChange={(e) => handleInputChange("paymentsRemaining", e.target.value)}
                  placeholder="Enter number of payments remaining"
                />
                <p className="text-xs text-muted-foreground">Override the database value if needed</p>
              </div>
            </div>
          </div>

          {/* Partial Agreement Specific Fields */}
          {agreementData.agreementType === "partial" && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Partial Purchase Specific Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Partial Dollar Amount</Label>
                  <Input
                    value={agreementData.partialDollarAmount || ""}
                    onChange={(e) => handleInputChange("partialDollarAmount", e.target.value)}
                    placeholder="Enter partial amount to purchase"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Partial End Date</Label>
                  <Input
                    value={agreementData.partialEndDate || ""}
                    onChange={(e) => handleInputChange("partialEndDate", e.target.value)}
                    placeholder="Enter end date for partial purchase"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Private Investor Agreement Specific Fields */}
          {agreementData.agreementType === "private" && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Private Investor Agreement Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Buyer Name</Label>
                  <Input
                    value={agreementData.buyerName || ""}
                    onChange={(e) => handleInputChange("buyerName", e.target.value)}
                    placeholder="Investment company or buyer name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Purchaser Name (Current Buyer Under Contract)</Label>
                  <Input
                    value={agreementData.purchaserName || ""}
                    onChange={(e) => handleInputChange("purchaserName", e.target.value)}
                    placeholder="Current buyer under the land contract"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Original Contract Date</Label>
                  <Input
                    type="date"
                    value={agreementData.contractDate || ""}
                    onChange={(e) => handleInputChange("contractDate", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Interest Rate (%)</Label>
                  <Input
                    value={agreementData.interestRate || ""}
                    onChange={(e) => handleInputChange("interestRate", e.target.value)}
                    placeholder="8"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Next Payment Due Date</Label>
                  <Input
                    type="date"
                    value={agreementData.nextPaymentDue || ""}
                    onChange={(e) => handleInputChange("nextPaymentDue", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Purchase Amount at Closing</Label>
                  <Input
                    value={agreementData.purchaseAmount || ""}
                    onChange={(e) => handleInputChange("purchaseAmount", e.target.value)}
                    placeholder="100600.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Liquidated Damages</Label>
                  <Input
                    value={agreementData.liquidatedDamages || ""}
                    onChange={(e) => handleInputChange("liquidatedDamages", e.target.value)}
                    placeholder="500.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Drafted By</Label>
                  <Input
                    value={agreementData.draftedBy || ""}
                    onChange={(e) => handleInputChange("draftedBy", e.target.value)}
                    placeholder="Your name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Reference Number</Label>
                  <Input
                    value={agreementData.referenceNumber || ""}
                    onChange={(e) => handleInputChange("referenceNumber", e.target.value)}
                    placeholder="586002"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Email-Ready Offer Letter */}
          {selectedDealId && (
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email-Ready Offer Letter
                </CardTitle>
                <p className="text-sm text-green-700 mt-2">Click the text area below to automatically copy a professional offer letter for email</p>
              </CardHeader>
              <CardContent>
                {(() => {
                  const selectedDeal = deals.find(deal => deal.id === selectedDealId);
                  const breakdown = calculateFinancialBreakdown(selectedDeal);
                  
                  if (!breakdown || !selectedDeal) return <p>No deal selected or insufficient data</p>;

                  const generateOfferLetter = () => {
                    const currentDate = new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });

                    return `Subject: Purchase Proposal for ${agreementData.propertyAddress || selectedDeal.property_address}

Dear ${agreementData.primarySellerName},

We are pleased to present our formal purchase proposal for your ${agreementData.paperType || 'land contract'} on the property referenced below. After thorough review and evaluation, we are prepared to move forward with a competitive cash offer.

PROPERTY: ${agreementData.propertyAddress || selectedDeal.property_address}

PURCHASE OFFER: ${formatCurrency(parseFloat(agreementData.brokerOfferToSeller) || 0)}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TOTAL CASH FLOW ANALYSIS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Initial Down Payment Received:
${formatCurrency(breakdown.downPayment)}

Monthly Payments Collected to Date:
${formatCurrency(breakdown.totalMonthlyPaymentsReceived)} (${breakdown.monthsSinceSale} payments @ ${formatCurrency(breakdown.monthlyPayment)}/month)
${breakdown.monthsUntilFirstPayment > 0 ? `
Additional Payments Before Transfer:
${formatCurrency(breakdown.additionalPaymentsBeforeFirst)} (${breakdown.monthsUntilFirstPayment} payments @ ${formatCurrency(breakdown.monthlyPayment)}/month)
` : ''}
Cash Offer from Contract Funding Group:
${formatCurrency(breakdown.cashOfferAmount)}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TOTAL CASH RECEIVED: ${formatCurrency(breakdown.totalCashFlow)}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This comprehensive analysis reflects the complete cash benefit throughout your investment lifecycle, demonstrating the total value you will realize from this transaction.

KEY ADVANTAGES OF THIS OFFER:

• Immediate Liquidity: Convert future payment stream to cash today
• Risk Elimination: No exposure to buyer default or property issues  
• Professional Closing: Complete documentation and secure transfer process
• Streamlined Timeline: 30-45 day closing period with clear milestones

NEXT STEPS:

Upon your acceptance, we will immediately begin our due diligence process, which includes title verification, contract review, and payment history confirmation. The attached Purchase Agreement contains complete terms and conditions for your review.

This proposal remains valid for 30 days from ${currentDate} and represents a competitive offer based on current market conditions and your contract's performance history.

We welcome the opportunity to discuss any questions or provide additional information regarding this proposal. Please feel free to contact us at your convenience.

Respectfully,

${agreementData.brokerCompanyName || 'Contract Funding Group, LLC'}
Professional Note Purchasing Services
info@contractfundinggroup.com

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
This communication is confidential and intended solely for the addressee.`;
                  };

                  return (
                    <div className="space-y-4">
                      <Textarea
                        value={generateOfferLetter()}
                        readOnly
                        rows={25}
                        className="font-mono text-sm bg-white border-2 border-green-300 cursor-pointer hover:border-green-400 transition-colors"
                        onClick={(e) => {
                          e.currentTarget.select();
                          navigator.clipboard.writeText(generateOfferLetter());
                          toast({
                            title: "Offer Letter Copied! 📧",
                            description: "Professional offer letter copied to clipboard - ready to paste into email!",
                            duration: 3000,
                          });
                        }}
                      />
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-2">📋 Quick Summary:</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Property:</span> {agreementData.propertyAddress || selectedDeal.property_address}
                          </div>
                          <div>
                            <span className="font-medium">Cash Offer:</span> {formatCurrency(parseFloat(agreementData.brokerOfferToSeller) || 0)}
                          </div>
                          <div>
                            <span className="font-medium">Total Benefit:</span> {formatCurrency(breakdown.totalCashFlow)}
                          </div>
                          <div>
                            <span className="font-medium">Transfer Date:</span> {agreementData.firstPaymentDate || 'TBD'}
                          </div>
                        </div>
                        <p className="text-xs text-green-600 mt-3 italic">
                          ✨ Click the text area above to copy this professional offer letter directly to your email
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* AI Enhancement Section */}
          {selectedDealId && (
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-purple-800 flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  AI-Powered Enhancements
                </CardTitle>
                <p className="text-sm text-purple-700 mt-2">Use AI to improve your agreement communications</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    onClick={handleGenerateCoverLetter}
                    disabled={aiGenerating}
                    variant="outline"
                    className="border-purple-300 hover:bg-purple-100"
                  >
                    {aiGenerating ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> Generate AI Cover Letter</>
                    )}
                  </Button>
                  <Button
                    onClick={handleReviewAgreement}
                    disabled={aiGenerating}
                    variant="outline"
                    className="border-purple-300 hover:bg-purple-100"
                  >
                    {aiGenerating ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reviewing...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> AI Agreement Review</>
                    )}
                  </Button>
                </div>
                {aiCoverLetter && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      AI-Generated Content
                    </h4>
                    <div className="whitespace-pre-wrap text-sm">{aiCoverLetter}</div>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(aiCoverLetter);
                        toast({
                          title: "Copied to Clipboard! ✨",
                          description: "AI-generated content copied and ready to use.",
                        });
                      }}
                      variant="outline"
                      size="sm"
                      className="mt-3"
                    >
                      Copy to Clipboard
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 flex-wrap">
            <Button onClick={() => setShowPreview(!showPreview)} variant="outline">
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? "Hide Preview" : "Show Preview"}
            </Button>
            <Button onClick={handleSaveAgreement} variant="outline" className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Save Agreement
            </Button>
            <Button onClick={handleEmailAgreement} variant="outline" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Package
            </Button>
            <Button onClick={handleDownloadWord} variant="default" className="flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              Download Word Document
            </Button>
            <Button onClick={handlePrintPackage} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Print Package
            </Button>
            <Button onClick={handlePrint} variant="outline" className="flex items-center gap-2">
              <Printer className="h-4 w-4" />
              Print Agreement Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Section */}
      {showPreview && (
        <Card>
          <CardHeader>
            <CardTitle>Agreement Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap font-mono text-sm bg-muted p-4 rounded-lg overflow-auto">
              {agreementData.agreementType === "partial" 
                ? generatePartialAgreement() 
                : agreementData.agreementType === "private"
                ? generatePrivateInvestorAgreement()
                : generateFullAgreement()}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Saved Agreements Section */}
      {savedAgreements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Saved Agreements</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Seller Name</TableHead>
                  <TableHead>Property Address</TableHead>
                  <TableHead>Agreement Type</TableHead>
                  <TableHead>Agreement Date</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {savedAgreements.map((agreement) => (
                  <TableRow key={agreement.id}>
                    <TableCell>{agreement.seller_name}</TableCell>
                    <TableCell className="max-w-xs truncate">{agreement.property_address}</TableCell>
                    <TableCell className="capitalize">{agreement.agreement_type}</TableCell>
                    <TableCell>
                      {agreement.agreement_date ? format(new Date(agreement.agreement_date), "MMM d, yyyy") : "-"}
                    </TableCell>
                    <TableCell>
                      {format(new Date(agreement.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => viewSavedAgreement(agreement)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Agreement</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the agreement for "{agreement.seller_name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteAgreement(agreement.id, agreement.seller_name)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete Agreement
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}